/**
 * ч.1327 — таблична частина документа (ТЧ) з механікою списків: ширини колонок, ручка на межі
 * заголовка, прокрутка вбік.
 *
 * ЧОМУ (зауваження власника): «ТЧ часто стискається через кількість знаків у назві колонки».
 * Звичайна таблиця (`table-layout: auto`, `width: 100%`) роздає ширину за мінімальним вмістом:
 * поле вводу на всю комірку нічого не просить, а довгий заголовок просить свою довжину — тож
 * колонка «Номенклатура» з полем вибору стискалась до 85 px, а «Склад» роздувався до 182 px.
 *
 * ЯК ПРАЦЮЄ.
 *   • Ширину колонки визначає ВМІСТ рядків, а не заголовок: поле вибору номенклатури —
 *     ширше, сума — вужча, дата — під дату, галочка і «×» — вузькі; текст — за довжиною.
 *     Заголовок — не довше `HEADER_CAP`, довший обрізається «…» з повним текстом у підказці.
 *     Ширина з inline-стилю заголовка (`<th style={{ width: 200 }}>`) — нижня межа зручної.
 *   • У кожної колонки дві ширини: зручна і найменша читабельна (`KIND_MIN`). Поки
 *     користувач нічого не тягнув: зручні вміщаються в блок — залишок отримує найширша
 *     «текстова» колонка (вибір, поле, текст); не вміщаються — усі стискаються пропорційно,
 *     але не вужче за найменшу; не вміщаються й найменші — прокрутка вбік (рішення власника:
 *     у ТЧ без позначки «ще колонки →» і без «ширини колонок — як було», досить прокрутки).
 *     Заголовок на ширину не впливає нижче за найменшу — він обрізається «…».
 *   • Ручка на межі заголовка. Перше перетягування фіксує поточні ширини УСІХ колонок —
 *     далі як в Excel: ручка йде точно за курсором, решта колонок на місці. Подвійний клік по
 *     ручці — ширина цієї колонки «за вмістом».
 *   • Ширини — у профілі користувача (`uiPrefs`, родина `dt-widths:`), ключ — сторінка (без
 *     ідентифікаторів документа) + склад заголовків: однакові ТЧ мають однакові ширини.
 *
 * Розмітку рядків компонент не чіпає: читає DOM (вид вмісту комірок) і малює лише свій
 * `<colgroup>` і шар ручок поверх заголовка. Нові рядки, довідник, що догрузився, інші
 * заголовки — MutationObserver перераховує ширини (лише коли змінився склад, не текст).
 * Списки й календарі полів у комірках — у порталі (`useAnchoredPopup`), прокрутка їх не обрізає.
 */
import { Children, isValidElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import uiPrefs from '@/store/uiPrefs';

export type CellKind =
  | 'search' | 'select' | 'textarea' | 'text' | 'static' | 'date' | 'number' | 'button' | 'check' | 'none';

/** Що з цього може забрати залишок ширини (колонка «для тексту»). */
const FLEX_KINDS = new Set<CellKind>(['search', 'select', 'textarea', 'text', 'static']);
/** Пріоритет виду колонки, коли в рядках різне (порожній рядок «none» не перебиває поле). */
const KIND_RANK: CellKind[] = ['search', 'select', 'textarea', 'text', 'static', 'date', 'number', 'button', 'check', 'none'];

const MIN_COL = 32;
const MAX_COL = 900;
const HEADER_CAP = 180;      // довший заголовок — «…» з підказкою
const MAX_DEFAULT = 420;
const BODY_ROWS = 25;        // скільки рядків дивитись для ширини

export interface GridCell {
  el: HTMLTableCellElement;
  col: number;
  span: number;
  section: 'head' | 'body' | 'foot';
}

/** Сітка таблиці з урахуванням colSpan / rowSpan: колонка кожної комірки. */
export function tableGrid(table: HTMLTableElement, bodyRows = BODY_ROWS): { cols: number; cells: GridCell[] } {
  const cells: GridCell[] = [];
  let cols = 0;
  const walk = (rows: HTMLTableRowElement[], section: GridCell['section']) => {
    const busy: Set<number>[] = rows.map(() => new Set());
    rows.forEach((tr, r) => {
      let c = 0;
      for (const el of Array.from(tr.cells)) {
        while (busy[r]!.has(c)) c += 1;
        const span = Math.max(1, el.colSpan || 1);
        const rs = Math.max(1, el.rowSpan || 1);
        for (let k = 1; k < rs && r + k < rows.length; k += 1) {
          for (let s = 0; s < span; s += 1) busy[r + k]!.add(c + s);
        }
        cells.push({ el, col: c, span, section });
        c += span;
      }
      cols = Math.max(cols, c, ...Array.from(busy[r]!).map((x) => x + 1));
    });
  };
  if (table.tHead) walk(Array.from(table.tHead.rows), 'head');
  const body: HTMLTableRowElement[] = [];
  for (const tb of Array.from(table.tBodies)) {
    for (const tr of Array.from(tb.rows)) { if (body.length < bodyRows) body.push(tr); }
  }
  walk(body, 'body');
  if (table.tFoot) walk(Array.from(table.tFoot.rows), 'foot');
  return { cols, cells };
}

const visibleInputs = (td: Element) =>
  Array.from(td.querySelectorAll('input')).filter((i) => i.type !== 'hidden');

/** Вид вмісту комірки — від нього залежить ширина колонки. */
export function cellKind(td: Element): CellKind {
  if (td.querySelector('.search-select')) return 'search';
  if (td.querySelector('.date-popover')) return 'date';
  if (td.querySelector('textarea')) return 'textarea';
  if (td.querySelector('select, .nice-select')) return 'select';
  const inputs = visibleInputs(td);
  if (inputs.length) {
    if (inputs.every((i) => i.type === 'checkbox' || i.type === 'radio')) return 'check';
    if (inputs.some((i) => i.type === 'number' || i.inputMode === 'decimal' || i.inputMode === 'numeric')) {
      return 'number';
    }
    return 'text';
  }
  const text = (td.textContent ?? '').trim();
  const buttons = Array.from(td.querySelectorAll('button'));
  if (buttons.length) {
    const btnText = buttons.map((b) => (b.textContent ?? '').trim()).join('').replace(/\s+/g, '');
    if (text.replace(/\s+/g, '') === btnText) return 'button';
  }
  return text ? 'static' : 'none';
}

const rank = (k: CellKind) => KIND_RANK.indexOf(k);
const pxOf = (v: string) => (/^\d+(\.\d+)?px$/.test(v) ? parseFloat(v) : 0);
const squash = (s: string) => s.replace(/\s+/g, ' ').trim();

/** Підпис складу таблиці: заголовки + кількість колонок (ключ збереження ширин). */
export function headSignature(table: HTMLTableElement, grid = tableGrid(table)): string {
  const heads = grid.cells.filter((c) => c.section === 'head').map((c) => squash(c.el.textContent ?? ''));
  return `${heads.join('|')}#${grid.cols}`;
}

/** Повний підпис: склад + вид кожної колонки + скільки варіантів у списках (догрузився довідник). */
function fullSignature(table: HTMLTableElement, grid: ReturnType<typeof tableGrid>): { head: string; full: string } {
  const head = headSignature(table, grid);
  const kinds: CellKind[] = Array.from({ length: grid.cols }, () => 'none');
  const opts: number[] = Array.from({ length: grid.cols }, () => 0);
  for (const c of grid.cells) {
    if (c.section === 'head' || c.span !== 1) continue;
    const k = cellKind(c.el);
    if (rank(k) < rank(kinds[c.col]!)) kinds[c.col] = k;
    const sel = c.el.querySelector('select');
    if (sel) opts[c.col] = Math.max(opts[c.col]!, sel.options.length);
  }
  return { head, full: `${head}~${kinds.join(',')}~${opts.join(',')}` };
}

export interface TableScan {
  head: string;
  sig: string;
  /** Зручна ширина колонки (за вмістом і заголовком). */
  defaults: number[];
  /** Найменша ще читабельна: до неї колонки стискаються, перш ніж зʼявиться прокрутка. */
  mins: number[];
  kinds: CellKind[];
}

export interface MeasureItem { text: string; el: Element }
export type Measure = (items: MeasureItem[]) => number[];

/** Найменша читабельна ширина за видом (px при шрифті 16). */
const KIND_MIN: Record<CellKind, number> = {
  search: 180, select: 100, textarea: 160, text: 110, static: 72, date: 112,
  number: 76, button: 32, check: 30, none: 32,
};

/** Ширини «за вмістом» для кожної колонки. `measure` — ширина тексту шрифтом елемента. */
export function scanTable(table: HTMLTableElement, measure: Measure, scale = 1): TableScan {
  const grid = tableGrid(table);
  const { head, full } = fullSignature(table, grid);
  const n = grid.cols;
  const kinds: CellKind[] = Array.from({ length: n }, () => 'none');
  const want: number[] = Array.from({ length: n }, () => MIN_COL);
  const floor: number[] = Array.from({ length: n }, () => 0);     // ширина з inline-стилю заголовка
  const need: number[] = Array.from({ length: n }, () => 0);      // щоб значення в полі не обрізалось
  const items: MeasureItem[] = [];
  const after: Array<(w: number) => void> = [];
  const ask = (text: string, el: Element, then: (w: number) => void) => {
    items.push({ text: text.slice(0, 200), el });
    after.push(then);
  };
  const raise = (col: number, w: number) => { want[col] = Math.max(want[col]!, Math.round(w)); };
  const s = (px: number) => px * scale;

  for (const c of grid.cells) {
    if (c.span !== 1) continue;
    const { el, col } = c;
    if (c.section === 'head') {
      floor[col] = Math.max(floor[col]!, pxOf(el.style.width), pxOf(el.style.minWidth));
      const text = squash(el.textContent ?? '');
      if (text) ask(text, el, (w) => raise(col, Math.min(w + s(20), s(HEADER_CAP))));
      continue;
    }
    const k = cellKind(el);
    if (rank(k) < rank(kinds[col]!)) kinds[col] = k;
    const extraBtns = k === 'button' ? 0 : Math.min(2, el.querySelectorAll('button').length
      - (k === 'search' ? el.querySelectorAll('.search-select button').length : 0)
      - (k === 'date' ? el.querySelectorAll('.date-popover button').length : 0));
    const plus = Math.max(0, extraBtns) * s(30);
    const input = visibleInputs(el)[0];
    const fixedInput = input ? pxOf(input.style.width) : 0;
    switch (k) {
      case 'search':
        raise(col, s(240) + plus);
        if (input?.value) ask(input.value, input, (w) => raise(col, Math.min(w + s(44), s(380)) + plus));
        break;
      case 'date': raise(col, s(124) + plus); break;
      case 'textarea': raise(col, s(240)); break;
      case 'check': raise(col, s(34)); break;
      case 'select': {
        const sel = el.querySelector('select');
        const labels = sel ? Array.from(sel.options).map((o) => squash(o.textContent ?? '')) : [];
        const longest = labels.reduce((a, b) => (b.length > a.length ? b : a), '');
        raise(col, s(110) + plus);
        if (longest && sel) ask(longest, sel, (w) => raise(col, Math.min(w + s(40), s(280)) + plus));
        break;
      }
      case 'number':
        raise(col, (fixedInput ? fixedInput + s(10) : s(90)) + plus);
        if (input?.value && !fixedInput) {
          ask(input.value, input, (w) => {
            raise(col, Math.min(w + s(28), s(160)) + plus);
            need[col] = Math.max(need[col]!, Math.min(w + s(24), s(130)) + plus);
          });
        }
        break;
      case 'text':
        raise(col, (fixedInput ? fixedInput + s(10) : s(150)) + plus);
        if (input?.value && !fixedInput) ask(input.value, input, (w) => raise(col, Math.min(w + s(28), s(320)) + plus));
        break;
      case 'button': ask(squash(el.textContent ?? ''), el, (w) => raise(col, w + s(22))); break;
      case 'static': ask(squash(el.textContent ?? ''), el, (w) => raise(col, Math.min(w + s(16), s(320)))); break;
      default: break;
    }
  }
  const widths = measure(items);
  widths.forEach((w, i) => after[i]!(w));
  const defaults = want.map((w, i) => Math.min(Math.max(w, MIN_COL, floor[i]!), Math.max(s(MAX_DEFAULT), floor[i]!)));
  // Ширина з заголовка в коді — лише «зручна»: її ставили проти стискання, тож нижньою межею
  // вона заважала б колонкам ужатись до читабельних, коли блок вузький.
  const mins = defaults.map((d, i) => Math.min(d, Math.max(s(KIND_MIN[kinds[i]!]), need[i]!)));
  return { head, sig: full, kinds, defaults, mins };
}

/** Хто забирає залишок ширини: найширша «текстова» колонка без зафіксованої ширини; у
 *  порожній таблиці (новий документ, рядків ще немає) — найширша за заголовком. */
export function flexColumn(scan: Pick<TableScan, 'kinds' | 'defaults'>, saved: Record<string, number> = {}): number {
  let flex = -1;
  const free = (i: number) => saved[i] == null;
  scan.kinds.forEach((k, i) => {
    if (free(i) && FLEX_KINDS.has(k) && (flex < 0 || scan.defaults[i]! > scan.defaults[flex]!)) flex = i;
  });
  if (flex < 0 && scan.kinds.every((k) => k === 'none')) {
    scan.defaults.forEach((w, i) => { if (free(i) && (flex < 0 || w > scan.defaults[flex]!)) flex = i; });
  }
  return flex;
}

/** Ширини колонок під блок завширшки `box`:
 *   • зручні вміщаються — залишок отримує `flex` (найширша «текстова» колонка);
 *   • не вміщаються — усі стискаються пропорційно до найменших читабельних;
 *   • не вміщаються й найменші — найменші, таблиця прокручується вбік. */
export function fitWidths(pref: number[], mins: number[], box: number, flex: number): number[] {
  const sumP = pref.reduce((a, b) => a + b, 0);
  const sumM = mins.reduce((a, b) => a + b, 0);
  if (!box || box <= sumM) return [...mins];
  if (box >= sumP) {
    const out = [...pref];
    if (flex >= 0) out[flex] = out[flex]! + Math.floor(box - sumP);
    return out;
  }
  const k = (box - sumM) / (sumP - sumM);
  const out = pref.map((p, i) => Math.floor(mins[i]! + (p - mins[i]!) * k));
  const left = Math.floor(box) - out.reduce((a, b) => a + b, 0);
  const to = flex >= 0 ? flex : out.length - 1;
  out[to] = out[to]! + Math.max(0, left);
  return out;
}

/** ч.1327: поле в комірці без підпису отримує назву колонки і номер рядка («Ціна, рядок 1»).
 *  Без цього в дереві доступності поле зветься своїм значенням («0,0000») — екранний диктор не каже,
 *  що це, а агент відео-інструкцій вводив ціну в поле знижки. Власний aria-label сторінки не чіпаємо. */
export function labelCellFields(table: HTMLTableElement, grid = tableGrid(table)): void {
  const header: string[] = [];
  for (const c of grid.cells) {
    if (c.section === 'head' && c.span === 1) header[c.col] = squash(c.el.textContent ?? '');
  }
  for (const c of grid.cells) {
    if (c.section === 'head' || c.span !== 1 || !header[c.col]) continue;
    const row = (c.el.parentElement as HTMLTableRowElement | null)?.sectionRowIndex ?? 0;
    const name = `${header[c.col]}, рядок ${row + 1}`;
    for (const f of Array.from(c.el.querySelectorAll('input, select, textarea'))) {
      const el = f as HTMLElement;
      if ((el as HTMLInputElement).type === 'hidden') continue;
      const ours = el.dataset.tchLabel === '1';
      if (!ours && (el.hasAttribute('aria-label') || el.hasAttribute('aria-labelledby') || el.id)) continue;
      if (el.getAttribute('aria-label') !== name) el.setAttribute('aria-label', name);
      el.dataset.tchLabel = '1';
    }
  }
}

/** Ширина тексту шрифтом елемента — прихованими span одним проходом (один перерахунок). */
const domMeasure: Measure = (items) => {
  if (!items.length || typeof document === 'undefined') return items.map(() => 0);
  const box = document.createElement('div');
  box.setAttribute('aria-hidden', 'true');
  box.style.cssText = 'position:fixed;left:-10000px;top:0;visibility:hidden;pointer-events:none;'
    + 'white-space:pre;contain:layout style;';
  const styles = new Map<Element, CSSStyleDeclaration>();
  const spans = items.map(({ text, el }) => {
    let cs = styles.get(el);
    if (!cs) { cs = getComputedStyle(el); styles.set(el, cs); }
    const sp = document.createElement('span');
    sp.textContent = text;
    sp.style.fontFamily = cs.fontFamily;
    sp.style.fontSize = cs.fontSize;
    sp.style.fontWeight = cs.fontWeight;
    sp.style.fontStyle = cs.fontStyle;
    sp.style.letterSpacing = cs.letterSpacing;
    sp.style.display = 'inline-block';
    box.appendChild(sp);
    return sp;
  });
  document.body.appendChild(box);
  const out = spans.map((sp) => sp.getBoundingClientRect().width);
  box.remove();
  return out;
};

/** Сторінка без ідентифікаторів: `/goods-sales/0190…` і `/goods-sales/new` — одна ТЧ. */
export function routeKey(pathname: string): string {
  return pathname.split('/')
    .map((p) => (/^[0-9a-f]{8}-[0-9a-f-]{20,}$/i.test(p) || /^\d+$/.test(p) || p === 'new' ? ':id' : p))
    .join('/');
}

function hash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

const WRAP_STYLE_KEYS = new Set([
  'margin', 'marginTop', 'marginBottom', 'marginLeft', 'marginRight', 'maxWidth',
]);

type Props = React.TableHTMLAttributes<HTMLTableElement> & {
  /** Свій ключ збереження ширин (за замовчуванням — сторінка + склад заголовків). */
  widthsKey?: string;
};

export default function TchTable({ children, className, style, widthsKey, ...rest }: Props) {
  const tableRef = useRef<HTMLTableElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scan, setScan] = useState<TableScan | null>(null);
  const [saved, setSaved] = useState<Record<string, number>>({});
  const [boxW, setBoxW] = useState(0);
  const [headH, setHeadH] = useState(0);

  const ownColgroup = useMemo(
    () => Children.toArray(children).some((c) => isValidElement(c) && c.type === 'colgroup'),
    [children],
  );
  const { wrapStyle, tableStyle, noStretch } = useMemo(() => {
    const w: React.CSSProperties = {};
    const t: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(style ?? {})) {
      if (WRAP_STYLE_KEYS.has(k)) (w as Record<string, unknown>)[k] = v;
      else if (k !== 'width' && k !== 'minWidth') t[k] = v;
    }
    return { wrapStyle: w, tableStyle: t as React.CSSProperties, noStretch: style?.width === 'auto' };
  }, [style]);

  const prefKey = scan
    ? `dt-widths:tch:${widthsKey ?? routeKey(typeof window !== 'undefined' ? window.location.pathname : '')}|${hash(scan.head)}`
    : null;

  // Перерахунок: лише коли змінився склад (заголовки, вид колонок, довідник у списках).
  const scanRef = useRef<TableScan | null>(null);
  const rescan = useCallback(() => {
    const t = tableRef.current;
    if (!t || ownColgroup) return;
    setHeadH(t.tHead ? t.tHead.offsetHeight : 0);
    const grid = tableGrid(t);
    labelCellFields(t, grid);
    const prev = scanRef.current;
    if (prev && prev.sig === fullSignature(t, grid).full) return;
    const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const next = scanTable(t, domMeasure, root / 16);
    // Та сама таблиця догрузила рядки — колонки лише ширшають (без «стрибків» назад).
    if (prev && prev.head === next.head) {
      next.defaults = next.defaults.map((w, i) => Math.max(w, prev.defaults[i] ?? 0));
      next.mins = next.mins.map((w, i) => Math.max(w, prev.mins[i] ?? 0));
    }
    scanRef.current = next;
    setScan(next);
  }, [ownColgroup]);

  useLayoutEffect(() => { rescan(); }, [rescan]);
  useEffect(() => {
    const t = tableRef.current;
    if (!t || typeof MutationObserver === 'undefined') return undefined;
    let frame = 0;
    const mo = new MutationObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; rescan(); });
    });
    mo.observe(t, { childList: true, subtree: true, characterData: true });
    return () => { mo.disconnect(); if (frame) cancelAnimationFrame(frame); };
  }, [rescan]);

  // Збережені ширини цієї таблиці.
  useLayoutEffect(() => {
    if (!prefKey) return;
    let v: Record<string, number> | null = null;
    try { v = uiPrefs.get<Record<string, number>>(prefKey); } catch { v = null; }
    const ok: Record<string, number> = {};
    for (const [k, w] of Object.entries(v ?? {})) {
      if (typeof w === 'number' && w >= MIN_COL && w <= MAX_COL) ok[k] = w;
    }
    setSaved(ok);
  }, [prefKey]);

  // Ширина блоку (і висота заголовка для ручок).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const check = () => {
      setBoxW(el.clientWidth);
      const t = tableRef.current;
      if (t?.tHead) setHeadH(t.tHead.offsetHeight);
    };
    check();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    ro?.observe(el);
    if (tableRef.current) ro?.observe(tableRef.current);
    return () => { ro?.disconnect(); };
  }, []);

  const cols = scan?.defaults.length ?? 0;
  const { eff, total } = useMemo(() => {
    if (!scan || !cols) return { eff: [] as number[], total: 0 };
    // Після першого перетягування всі ширини зафіксовані (saved) — тоді pref = mins = saved.
    const pref = scan.defaults.map((d, i) => saved[i] ?? d);
    const mins = scan.mins.map((d, i) => saved[i] ?? d);
    const flex = flexColumn(scan, saved);
    const out = noStretch ? pref : fitWidths(pref, mins, boxW, flex);
    return { eff: out, total: out.reduce((a, b) => a + b, 0) };
  }, [scan, cols, saved, boxW, noStretch]);

  // Обрізаний заголовок — повний текст у підказці.
  useLayoutEffect(() => {
    const t = tableRef.current;
    if (!t?.tHead) return;
    for (const th of Array.from(t.tHead.querySelectorAll('th'))) {
      const mine = th.dataset.tchTitle === '1';
      if (th.hasAttribute('title') && !mine) continue;
      const cut = th.scrollWidth > th.clientWidth + 1;
      if (cut) { th.title = squash(th.textContent ?? ''); th.dataset.tchTitle = '1'; }
      else if (mine) { th.removeAttribute('title'); delete th.dataset.tchTitle; }
    }
  }, [eff]);

  const persist = useCallback((v: Record<string, number>) => {
    if (!prefKey) return;
    try {
      if (Object.keys(v).length) uiPrefs.set(prefKey, v); else uiPrefs.remove(prefKey);
    } catch { /* приватний режим — не критично */ }
  }, [prefKey]);

  const startResize = (col: number, e: React.PointerEvent<HTMLSpanElement>) => {
    if (e.button > 0) return;            // лише основна кнопка миші (дотик / перо — теж)
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    // Перше перетягування фіксує поточні ширини всіх колонок — далі ручка йде за курсором.
    const pinned: Record<string, number> = {};
    eff.forEach((w, i) => { pinned[i] = w; });
    const startW = eff[col] ?? MIN_COL;
    let cur = pinned;
    const onMove = (ev: PointerEvent) => {
      const w = Math.min(MAX_COL, Math.max(MIN_COL, Math.round(startW + ev.clientX - startX)));
      cur = { ...pinned, [col]: w };
      setSaved(cur);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.classList.remove('tch-resizing');
      persist(cur);
    };
    document.body.classList.add('tch-resizing');
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const fitColumn = (col: number) => {
    if (!scan) return;
    const next: Record<string, number> = {};
    eff.forEach((w, i) => { next[i] = w; });
    next[col] = scan.defaults[col] ?? MIN_COL;
    setSaved(next);
    persist(next);
  };

  const edges: number[] = [];
  eff.reduce((x, w) => { edges.push(x + w); return x + w; }, 0);
  const tableCls = ['tch', 'tch--sized', ...(className ?? '').split(/\s+/).filter((c) => c && c !== 'tch')].join(' ');

  return (
    <div className="tch-wrap" style={wrapStyle}>
      <div className="tch-scroll" ref={scrollRef}>
        <table
          ref={tableRef}
          {...rest}
          className={tableCls}
          style={{ ...tableStyle, width: total ? `${total}px` : undefined }}
        >
          {!ownColgroup && eff.length > 0 && (
            <colgroup>{eff.map((w, i) => <col key={i} style={{ width: `${w}px` }} />)}</colgroup>
          )}
          {children}
        </table>
        {!ownColgroup && headH > 0 && eff.length > 0 && (
          <div className="tch-handles" style={{ height: headH, width: total }}>
            {edges.map((x, i) => (
              <span
                key={i}
                className="tch-handle"
                style={{ left: x - 7 }}
                title="Перетягніть, щоб змінити ширину колонки. Подвійний клік — ширина за вмістом"
                onPointerDown={(e) => startResize(i, e)}
                onDoubleClick={() => fitColumn(i)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
